import {useState, useEffect, useRef, Fragment} from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useParams, useNavigate } from 'react-router-dom';
import getAdminTaskInstanceDetails from '../functions/API/getAdminTaskInstanceDetails.js'
import postInitiateStepSigning from '../functions/API/postInitiateStepSigning.js'
import postCheckStepSigning from '../functions/API/postCheckStepSigning.js'
import postSendStepSigning from '../functions/API/postSendStepSigning.js'
import postCancelStepSigning from '../functions/API/postCancelStepSigning.js'
import postUpdateTaskInstanceStatus from '../functions/API/postUpdateTaskInstanceStatus.js'
import postDeleteTaskInstance from '../functions/API/postDeleteTaskInstance.js'
import postUpdateStepStatus from '../functions/API/postUpdateStepStatus.js'
import postUpdateFieldValue from '../functions/API/postUpdateFieldValue.js'
import postCompleteLastStep from '../functions/API/postCompleteLastStep.js'
import getAssignmentHistory from '../functions/API/getAssignmentHistory.js'
import getTaskMessages from '../functions/API/getTaskMessages.js'
import postSendTaskMessage from '../functions/API/postSendTaskMessage.js'
import postDeleteTaskMessage from '../functions/API/postDeleteTaskMessage.js'
import postReviewStep from '../functions/API/postReviewStep.js'
import postReopenStep from '../functions/API/postReopenStep.js'
import postAddInstanceStep from '../functions/API/postAddInstanceStep.js'
import postGetLinkableFields from '../functions/API/postGetLinkableFields.js'
import postLinkToAgreement from '../functions/API/postLinkToAgreement.js'
import AdminSidebar from '../components/admin/AdminSidebar.js';
import { toast } from '../lib/toast.js';
import AssignTaskInstanceForm from '../forms/assignTaskInstance.js';
import AddInstanceFieldForm from '../forms/addInstanceField.js';
import AddInstanceStepForm from '../forms/addInstanceStep.js';
import Loader from '../components/loader.js'
import DeleteModal from '../modals/delete.js'
import CompletionMessageModal from '../modals/completionMessage.js'
import ReviewStepModal from '../modals/reviewStep.js'
import ReopenStepModal from '../modals/reopenStep.js'
import LinkToAgreementModal from '../modals/linkToAgreement.js'
import ConfirmationModal from '../modals/confirmation.js'
import { Button } from '../components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet'
import {
  ArrowLeft, Play, CheckCircle2, XCircle, UserPlus, Plus, Trash2, Clock,
  FileText, CreditCard, Upload, ExternalLink, FolderOpen, MessageSquare, Send, ChevronDown, Star, Zap, Circle, HelpCircle, RotateCcw, Info, Link2, PenTool, Users, ArrowRight
} from 'lucide-react'
import { INSTANCE_STATUSES, STEP_STATUSES, SOURCE_TYPES } from '../constants/taskInstanceStatuses.js'

export default function TaskInstanceDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [instance, setInstance] = useState(null);
    const [shouldRefresh, setShouldRefresh] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Sheet states
    const [assignSheetOpen, setAssignSheetOpen] = useState(false);
    const [addFieldSheetOpen, setAddFieldSheetOpen] = useState(false);
    const [selectedStepId, setSelectedStepId] = useState(null);
    const [historySheetOpen, setHistorySheetOpen] = useState(false);
    const [assignmentHistory, setAssignmentHistory] = useState([]);

    // Messages
    const [messages, setMessages] = useState([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [newMessageBody, setNewMessageBody] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);

    // Admin field editing
    const [adminFieldValues, setAdminFieldValues] = useState({});
    const [submittingStep, setSubmittingStep] = useState(null);

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [fieldToDelete, setFieldToDelete] = useState(null);

    // Completion modal
    const [completionModalOpen, setCompletionModalOpen] = useState(false);
    const [completionModalLoading, setCompletionModalLoading] = useState(false);
    const [pendingLastStep, setPendingLastStep] = useState(null);

    // Review step modal
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [reviewStep, setReviewStep] = useState(null);
    const [reviewLoading, setReviewLoading] = useState(false);

    // Reopen step modal
    const [reopenModalOpen, setReopenModalOpen] = useState(false);
    const [reopenStep, setReopenStep] = useState(null);
    const [reopenReason, setReopenReason] = useState('');
    const [reopenLoading, setReopenLoading] = useState(false);

    // Add step sheet
    const [addStepSheetOpen, setAddStepSheetOpen] = useState(false);
    const [addStepAfterPosition, setAddStepAfterPosition] = useState(0);

    // Link to agreement modal
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkableFields, setLinkableFields] = useState([]);
    const [linkTenantUser, setLinkTenantUser] = useState(null);
    const [linkFieldsLoading, setLinkFieldsLoading] = useState(false);
    const [linkLoading, setLinkLoading] = useState(false);

    // Status dropdown
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

    // Outcome card state
    const [selectedOutcome, setSelectedOutcome] = useState(null);
    const [outcomeMessage, setOutcomeMessage] = useState('');
    const [outcomeLoading, setOutcomeLoading] = useState(false);

    // Delete instance modal
    const [deleteInstanceModalOpen, setDeleteInstanceModalOpen] = useState(false);

    // Cancel signing modal
    const [cancelSigningModalOpen, setCancelSigningModalOpen] = useState(false);
    const [cancelSigningStepId, setCancelSigningStepId] = useState(null);

    // Initiate signing confirmation modal
    const [signingConfirmModalOpen, setSigningConfirmModalOpen] = useState(false);
    const [signingConfirmStep, setSigningConfirmStep] = useState(null);
    const [jointCustomerEmail, setJointCustomerEmail] = useState('');

    async function GetAPIData() {
        const response = await getAdminTaskInstanceDetails(parseInt(id));
        if (response.response.ok && response.data?.task_instance) {
            setInstance(response.data.task_instance);
        } else {
            toast.error('Failed to load task instance.');
        }
        setIsLoaded(true);
    }

    useEffect(() => {
        GetAPIData();
    }, [shouldRefresh]);

    // Load messages on mount and poll
    useEffect(() => {
        if (instance) {
            fetchMessages();
            const interval = setInterval(fetchMessages, 10000);
            return () => clearInterval(interval);
        }
    }, [instance?.id]);

    function refreshData() {
        setShouldRefresh(shouldRefresh + 1);
    }

    async function handleStatusChange(newStatus) {
        if (actionLoading) return;
        setActionLoading(true);
        setStatusDropdownOpen(false);

        const response = await postUpdateTaskInstanceStatus({
            id: instance.id,
            status: newStatus,
        });

        if (response.response.ok) {
            toast.success('Status updated.');
            refreshData();
        } else {
            toast.error('Failed to update status.');
        }
        setActionLoading(false);
    }

    async function handleDeleteInstance() {
        if (actionLoading) return;
        setActionLoading(true);

        const response = await postDeleteTaskInstance({ id: instance.id });

        if (response.response.ok) {
            toast.success('Task instance deleted.');
            navigate('/taskinstances');
        } else {
            toast.error('Failed to delete task instance.');
        }
        setActionLoading(false);
    }

    async function handleStepStatusChange(stepId, newStatus) {
        if (actionLoading) return;
        setActionLoading(true);

        const response = await postUpdateStepStatus({
            id: stepId,
            status: newStatus,
        });

        if (response.response.ok) {
            toast.success('Step completed.');
            refreshData();
        } else {
            toast.error('Failed to update step.');
        }
        setActionLoading(false);
    }

    function resolveAgreementName(stepConfig) {
        const name = stepConfig?.agreement_name || stepConfig?.template_name || 'Document for Signing';
        return name.replace(/\{\{(.+?)\}\}/g, (_, fieldName) => {
            const trimmed = fieldName.trim();
            for (const step of (instance?.steps || [])) {
                const field = (step.fields || []).find(f => f.name === trimmed);
                if (field) {
                    const val = renderFieldValue(field);
                    return (val && val !== '-') ? (typeof val === 'string' ? val : String(val)) : `[${trimmed}]`;
                }
            }
            return `[${trimmed}]`;
        });
    }

    function resolveFieldMappingValue(mapping) {
        if (mapping.source_type === 'static_value') {
            return mapping.static_value ?? '';
        }
        if (mapping.source_type === 'customer_enters') {
            return null; // customer fills in during signing
        }
        if (mapping.source_type === 'task_data' && mapping.source_step_id && mapping.source_field_id) {
            const sourceStep = (instance?.steps || []).find(s => s.task_flow_step_id === mapping.source_step_id);
            if (!sourceStep) return '(step not found)';
            const sourceField = (sourceStep.fields || []).find(f => f.task_flow_step_field_id === mapping.source_field_id);
            if (!sourceField) return '(field not found)';
            const val = renderFieldValue(sourceField);
            return typeof val === 'string' ? val : String(val ?? '-');
        }
        return '';
    }

    function handleInitiateDocumentSigning(step) {
        if (actionLoading) return;
        setSigningConfirmStep(step);
        setJointCustomerEmail('');
        setSigningConfirmModalOpen(true);
    }

    async function confirmInitiateDocumentSigning() {
        if (!signingConfirmStep) return;

        // Validate joint customer email if provided
        if (jointCustomerEmail && !jointCustomerEmail.includes('@')) {
            toast.error('Please enter a valid email address for the joint customer.');
            return;
        }

        setActionLoading(true);
        setSigningConfirmModalOpen(false);
        try {
            const payload = { task_instance_step_id: signingConfirmStep.id };
            if (jointCustomerEmail) {
                payload.joint_customer_email = jointCustomerEmail;
            }
            const response = await postInitiateStepSigning(payload);
            if (response.response.ok) {
                toast.success('Draft agreement created. Review and send when ready.');
                refreshData();
            } else {
                toast.error(response.data?.message || 'Failed to create draft.');
            }
        } catch {
            toast.error('Failed to create draft agreement.');
        }
        setActionLoading(false);
        setSigningConfirmStep(null);
    }

    async function handleSendForSigning(stepId) {
        if (actionLoading) return;
        setActionLoading(true);
        try {
            const response = await postSendStepSigning({ task_instance_step_id: stepId });
            if (response.response.ok) {
                toast.success('Document sent for signing.');
                refreshData();
            } else {
                toast.error(response.data?.message || 'Failed to send for signing.');
            }
        } catch {
            toast.error('Failed to send document for signing.');
        }
        setActionLoading(false);
    }

    async function handleReviewDraft(stepId) {
        if (actionLoading) return;
        // Open window immediately (before async fetch) to avoid popup blocker
        const previewWindow = window.open('about:blank', '_blank');
        setActionLoading(true);
        try {
            const apiToken = localStorage.getItem('apiToken');
            const url = `${process.env.REACT_APP_API_URL}/api/${process.env.REACT_APP_API_VERSION}/taskinstances/steps/signing/preview`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + apiToken,
                },
                body: JSON.stringify({ task_instance_step_id: stepId }),
            });
            if (response.ok) {
                const blob = await response.blob();
                const pdfBlob = new Blob([blob], { type: 'application/pdf' });
                const blobUrl = URL.createObjectURL(pdfBlob);
                if (previewWindow) {
                    previewWindow.location.href = blobUrl;
                }
            } else {
                if (previewWindow) previewWindow.close();
                const data = await response.json().catch(() => null);
                toast.error(data?.message || 'Unable to get document preview.');
            }
        } catch {
            if (previewWindow) previewWindow.close();
            toast.error('Failed to get document preview.');
        }
        setActionLoading(false);
    }

    async function handleCheckSigningStatus(stepId) {
        if (actionLoading) return;
        setActionLoading(true);
        try {
            const response = await postCheckStepSigning({ task_instance_step_id: stepId });
            if (response.response.ok) {
                const status = response.data?.status;
                const currentSigner = response.data?.current_signer;
                if (status === 'signed') {
                    toast.success('Document has been signed.');
                } else if (status === 'draft') {
                    toast.info('Agreement is in draft. Review and send when ready.');
                } else if (status === 'sent') {
                    if (currentSigner === 'admin' && response.data?.signing_url) {
                        toast.info('It\'s your turn to sign.');
                        window.open(response.data.signing_url, '_blank');
                    } else if (currentSigner === 'customer') {
                        toast.info('Waiting for customer to sign.');
                    } else {
                        toast.info('Document is still awaiting signature.');
                        if (response.data?.signing_url) {
                            window.open(response.data.signing_url, '_blank');
                        }
                    }
                } else {
                    toast.info(`Signing status: ${status}`);
                }
                refreshData();
            } else {
                toast.error(response.data?.message || 'Failed to check signing status.');
            }
        } catch {
            toast.error('Failed to check signing status.');
        }
        setActionLoading(false);
    }

    async function handleAdminSign(stepId) {
        if (actionLoading) return;
        setActionLoading(true);
        try {
            const response = await postCheckStepSigning({ task_instance_step_id: stepId });
            if (response.response.ok && response.data?.signing_url) {
                window.open(response.data.signing_url, '_blank');
            } else {
                toast.error(response.data?.message || 'Unable to get signing link.');
            }
            refreshData();
        } catch {
            toast.error('Failed to open signing document.');
        }
        setActionLoading(false);
    }

    function handleCancelDocumentSigning(stepId) {
        if (actionLoading) return;
        setCancelSigningStepId(stepId);
        setCancelSigningModalOpen(true);
    }

    async function confirmCancelDocumentSigning() {
        if (!cancelSigningStepId) return;
        setActionLoading(true);
        try {
            const response = await postCancelStepSigning({ task_instance_step_id: cancelSigningStepId });
            if (response.response.ok) {
                toast.success('Document signing has been cancelled.');
                refreshData();
            } else {
                toast.error(response.data?.message || 'Failed to cancel signing.');
            }
        } catch {
            toast.error('Failed to cancel document signing.');
        }
        setActionLoading(false);
        setCancelSigningStepId(null);
    }

    async function handleViewHistory() {
        const response = await getAssignmentHistory(instance.id);
        if (response.response.ok && response.data?.assignment_history) {
            setAssignmentHistory(response.data.assignment_history);
        }
        setHistorySheetOpen(true);
    }

    // --- Messages ---

    async function fetchMessages() {
        const response = await getTaskMessages(instance?.id || parseInt(id));
        if (response.response.ok && response.data?.messages) {
            setMessages(response.data.messages);
        }
        setMessagesLoading(false);
    }


    async function handleSendMessage() {
        if (!newMessageBody.trim() || sendingMessage) return;
        setSendingMessage(true);

        const response = await postSendTaskMessage({
            task_instance_id: instance.id,
            body: newMessageBody.trim(),
        });
        if (response.response.ok) {
            setNewMessageBody('');
            await fetchMessages();
        } else {
            toast.error('Failed to send message.');
        }
        setSendingMessage(false);
    }

    async function handleDeleteMessage(msgId) {
        const response = await postDeleteTaskMessage(msgId);
        if (response.response.ok) {
            setMessages(messages.filter(m => m.id !== msgId));
        } else {
            toast.error('Failed to delete message.');
        }
    }

    // --- Admin field editing ---

    function updateAdminFieldValue(fieldId, value) {
        setAdminFieldValues(prev => ({ ...prev, [fieldId]: value }));
    }

    function getFieldOptions(field) {
        const rules = field.validation_rules_snapshot;
        if (!rules) return [];
        try {
            const parsed = typeof rules === 'string' ? JSON.parse(rules) : rules;
            return parsed?.options || [];
        } catch { return []; }
    }

    async function handleAdminSubmitStep(step) {
        const fields = step.fields || [];
        const requiredFields = fields.filter(f => f.is_required_snapshot);
        const missing = requiredFields.filter(f => {
            const val = adminFieldValues[f.id];
            if (val === undefined || val === null || val === '') return true;
            if (Array.isArray(val) && val.length === 0) return true;
            return false;
        });

        if (missing.length > 0) {
            toast.error(`Please complete: ${missing.map(f => f.name_snapshot).join(', ')}`);
            return;
        }

        setSubmittingStep(step.id);

        // Save all field values
        for (const field of fields) {
            const val = adminFieldValues[field.id];
            if (val !== undefined && val !== null) {
                await postUpdateFieldValue({ id: field.id, value: val });
            }
        }

        // Mark step complete (outcome card handles completion of the instance)
        await handleStepStatusChange(step.id, 'COMPLETED');
        setSubmittingStep(null);
    }

    async function handleOutcomeConfirm() {
        if (!selectedOutcome || outcomeLoading) return;
        setOutcomeLoading(true);

        const response = await postUpdateTaskInstanceStatus({
            id: instance.id,
            status: 'COMPLETED',
            completion_outcome: selectedOutcome,
            completion_message: outcomeMessage || null,
        });

        if (response.response.ok) {
            toast.success('Request completed.');
            setSelectedOutcome(null);
            setOutcomeMessage('');
            refreshData();
        } else {
            toast.error('Failed to complete request.');
        }
        setOutcomeLoading(false);
    }

    async function handleCompleteLastStep(outcome, message) {
        if (!pendingLastStep) return;
        setCompletionModalLoading(true);

        const response = await postCompleteLastStep({
            step_id: pendingLastStep.id,
            completion_outcome: outcome,
            completion_message: message || null,
        });

        if (response.response.ok) {
            toast.success('Task instance completed.');
            setCompletionModalOpen(false);
            setPendingLastStep(null);
            refreshData();
        } else {
            toast.error('Failed to complete task instance.');
        }
        setCompletionModalLoading(false);
    }

    async function handleStatusCompleteWithOutcome(outcome, message) {
        setCompletionModalLoading(true);

        const response = await postUpdateTaskInstanceStatus({
            id: instance.id,
            status: 'COMPLETED',
            completion_outcome: outcome,
            completion_message: message || null,
        });

        if (response.response.ok) {
            toast.success('Task instance completed.');
            setCompletionModalOpen(false);
            refreshData();
        } else {
            toast.error('Failed to complete task instance.');
        }
        setCompletionModalLoading(false);
    }

    function handleAddField(stepId) {
        setSelectedStepId(stepId);
        setAddFieldSheetOpen(true);
    }

    function handleDeleteField(field) {
        setFieldToDelete(field);
        setDeleteModalOpen(true);
    }

    async function handleApproveStep(step) {
        if (actionLoading) return;
        setActionLoading(true);

        const response = await postReviewStep({
            step_id: step.id,
            action: 'approve',
        });

        if (response.response.ok) {
            toast.success('Step approved.');
            refreshData();
        } else {
            toast.error('Failed to approve step.');
        }
        setActionLoading(false);
    }

    function handleRejectStep(step) {
        setReviewStep(step);
        setReviewModalOpen(true);
    }

    async function handleReviewConfirm(action, message) {
        if (!reviewStep) return;
        setReviewLoading(true);

        const response = await postReviewStep({
            step_id: reviewStep.id,
            action: action,
            message: message,
        });

        if (response.response.ok) {
            toast.success(action === 'reject_resubmit' ? 'Step returned for resubmission.' : 'Request closed.');
            setReviewModalOpen(false);
            setReviewStep(null);
            refreshData();
        } else {
            toast.error('Failed to process review.');
        }
        setReviewLoading(false);
    }

    function handleReopenStep(step) {
        setReopenStep(step);
        setReopenReason('');
        setReopenModalOpen(true);
    }

    async function handleReopenConfirm() {
        if (!reopenStep) return;
        setReopenLoading(true);

        const response = await postReopenStep({
            step_id: reopenStep.id,
            reason: reopenReason || undefined,
        });

        if (response.response.ok) {
            toast.success('Step reopened.');
            setReopenModalOpen(false);
            setReopenStep(null);
            setReopenReason('');
            refreshData();
        } else {
            toast.error('Failed to reopen step.');
        }
        setReopenLoading(false);
    }

    function handleAddStep(position) {
        setAddStepAfterPosition(position);
        setAddStepSheetOpen(true);
    }

    async function handleOpenLinkModal() {
        setLinkModalOpen(true);
        setLinkFieldsLoading(true);

        const response = await postGetLinkableFields({
            task_instance_id: instance.id,
        });

        if (response.response.ok && response.data) {
            setLinkableFields(response.data.fields || []);
            setLinkTenantUser(response.data.tenant_user || null);
        } else {
            toast.error('Failed to load field values.');
        }
        setLinkFieldsLoading(false);
    }

    async function handleLinkConfirm({ firstname, lastname, dob, agreementreference }) {
        setLinkLoading(true);

        const response = await postLinkToAgreement({
            task_instance_id: instance.id,
            firstname,
            lastname,
            dob,
            agreementreference,
        });

        if (response.response.ok) {
            toast.success('User linked to agreement successfully.');
            setLinkModalOpen(false);
            refreshData();
        } else {
            const errorMsg = response.data?.error || 'Failed to link user to agreement.';
            toast.error(errorMsg);
        }
        setLinkLoading(false);
    }

    function formatDate(dateStr) {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString();
    }

    function formatDateTime(dateStr) {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString();
    }

    // --- Field display ---

    function renderFieldValue(field) {
        const type = field.field_type_snapshot;

        if (type === 'BOOLEAN') {
            return field.value_boolean !== null ? (field.value_boolean ? 'Yes' : 'No') : '-';
        }
        if (type === 'DATE') {
            return field.value_date ? formatDate(field.value_date) : '-';
        }
        if (type === 'NUMBER') {
            return field.value_number != null ? field.value_number : '-';
        }
        if (type === 'CURRENCY') {
            return field.value_number != null ? `£${parseFloat(field.value_number).toFixed(2)}` : '-';
        }
        if (type === 'FILE') {
            return field.value_file_url ? (
                <a href={field.value_file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                    View File <ExternalLink className="h-3 w-3" />
                </a>
            ) : '-';
        }
        if (type === 'DROPDOWN' || type === 'MULTI_SELECT') {
            if (field.value_json) {
                try {
                    const parsed = typeof field.value_json === 'string' ? JSON.parse(field.value_json) : field.value_json;
                    return Array.isArray(parsed) ? parsed.join(', ') : String(parsed);
                } catch {
                    return field.value_json;
                }
            }
            return '-';
        }
        return field.value_text || '-';
    }

    function renderAdminFieldInput(field) {
        const type = field.field_type_snapshot;
        const value = adminFieldValues[field.id] !== undefined ? adminFieldValues[field.id] : '';

        if (type === 'TEXT') {
            return (
                <input
                    type="text"
                    value={value}
                    onChange={(e) => updateAdminFieldValue(field.id, e.target.value)}
                    placeholder={field.placeholder_snapshot || 'Enter text'}
                    className="w-full text-sm border border-gray-300 dark:border-zinc-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent dark:bg-zinc-800 dark:text-white"
                />
            );
        }
        if (type === 'TEXTAREA') {
            return (
                <textarea
                    value={value}
                    onChange={(e) => updateAdminFieldValue(field.id, e.target.value)}
                    placeholder={field.placeholder_snapshot || 'Enter details'}
                    rows={3}
                    className="w-full text-sm border border-gray-300 dark:border-zinc-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent resize-none dark:bg-zinc-800 dark:text-white"
                />
            );
        }
        if (type === 'NUMBER') {
            return (
                <input
                    type="number"
                    value={value}
                    onChange={(e) => updateAdminFieldValue(field.id, e.target.value)}
                    placeholder={field.placeholder_snapshot || '0'}
                    className="w-full text-sm border border-gray-300 dark:border-zinc-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent dark:bg-zinc-800 dark:text-white"
                />
            );
        }
        if (type === 'CURRENCY') {
            return (
                <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 dark:text-gray-400">£</span>
                    <input
                        type="number"
                        value={value}
                        onChange={(e) => updateAdminFieldValue(field.id, e.target.value)}
                        placeholder={field.placeholder_snapshot || '0.00'}
                        step="0.01"
                        className="w-full text-sm border border-gray-300 dark:border-zinc-600 rounded-lg pl-7 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent dark:bg-zinc-800 dark:text-white"
                    />
                </div>
            );
        }
        if (type === 'DATE') {
            return (
                <input
                    type="date"
                    value={value}
                    onChange={(e) => updateAdminFieldValue(field.id, e.target.value)}
                    className="w-full text-sm border border-gray-300 dark:border-zinc-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent dark:bg-zinc-800 dark:text-white"
                />
            );
        }
        if (type === 'BOOLEAN') {
            return (
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={() => updateAdminFieldValue(field.id, true)}
                        className={`px-4 py-2 text-sm rounded-lg border transition-colors ${value === true ? 'bg-gray-100 dark:bg-zinc-700 border-gray-400 text-gray-900 dark:text-white font-medium' : 'border-gray-300 dark:border-zinc-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-zinc-800'}`}
                    >
                        Yes
                    </button>
                    <button
                        type="button"
                        onClick={() => updateAdminFieldValue(field.id, false)}
                        className={`px-4 py-2 text-sm rounded-lg border transition-colors ${value === false ? 'bg-gray-100 dark:bg-zinc-700 border-gray-400 text-gray-900 dark:text-white font-medium' : 'border-gray-300 dark:border-zinc-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-zinc-800'}`}
                    >
                        No
                    </button>
                </div>
            );
        }
        if (type === 'DROPDOWN') {
            const options = getFieldOptions(field);
            return (
                <div className="flex flex-wrap gap-2">
                    {options.map(opt => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => updateAdminFieldValue(field.id, opt.value)}
                            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${value === opt.value ? 'bg-gray-100 dark:bg-zinc-700 border-gray-400 text-gray-900 dark:text-white font-medium' : 'border-gray-300 dark:border-zinc-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-zinc-800'}`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            );
        }
        if (type === 'MULTI_SELECT') {
            const options = getFieldOptions(field);
            const selected = Array.isArray(value) ? value : [];
            return (
                <div className="flex flex-wrap gap-2">
                    {options.map(opt => {
                        const isSelected = selected.includes(opt.value);
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => {
                                    const updated = isSelected
                                        ? selected.filter(v => v !== opt.value)
                                        : [...selected, opt.value];
                                    updateAdminFieldValue(field.id, updated);
                                }}
                                className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${isSelected ? 'bg-gray-100 dark:bg-zinc-700 border-gray-400 text-gray-900 dark:text-white font-medium' : 'border-gray-300 dark:border-zinc-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-zinc-800'}`}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
            );
        }
        return (
            <input
                type="text"
                value={value}
                onChange={(e) => updateAdminFieldValue(field.id, e.target.value)}
                className="w-full text-sm border border-gray-300 dark:border-zinc-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent dark:bg-zinc-800 dark:text-white"
            />
        );
    }

    function getSkipReason(step) {
        const conditions = step.conditional_logic_snapshot;
        if (!conditions || !Array.isArray(conditions) || !instance?.steps) return null;
        const allFields = {};
        instance.steps.forEach(s => (s.fields || []).forEach(f => { allFields[f.id] = f; }));
        const parts = conditions.map(c => {
            const field = allFields[c.source_instance_field_id];
            if (!field) return null;
            return `'${field.name_snapshot}'`;
        }).filter(Boolean);
        if (parts.length === 0) return null;
        return `Based on ${[...new Set(parts)].join(' and ')} response`;
    }

    // --- Render ---

    if (!instance && isLoaded) {
        return (
            <AdminSidebar>
                <div className="text-center py-12">
                    <p className="text-sm text-gray-500">Task instance not found.</p>
                    <Button variant="outline" className="mt-4" onClick={() => navigate('/taskinstances')}>
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to Task Instances
                    </Button>
                </div>
            </AdminSidebar>
        );
    }

    const statusInfo = instance ? (INSTANCE_STATUSES[instance.status] || { label: instance.status, color: 'secondary' }) : {};
    const sortedSteps = instance?.steps ? [...instance.steps].sort((a, b) => (a.position_snapshot || 0) - (b.position_snapshot || 0)) : [];
    const visibleSteps = sortedSteps.filter(s => s.is_conditionally_visible);
    const completedCount = visibleSteps.filter(s => s.status === 'COMPLETED').length;
    const totalSteps = visibleSteps.length;

    // Outcome card state
    const allVisibleStepsComplete = visibleSteps.length > 0 && visibleSteps.every(s => s.status === 'COMPLETED');
    const isInstanceCompleted = instance?.status === 'COMPLETED';
    const isCancelled = instance?.status === 'CANCELLED';
    const isAutoCompleted = isInstanceCompleted && messages.some(m => m.sender_role === 'system' && (m.subject === 'Request Completed' || m.subject === 'Request Closed'));

    return (
        <>
            <AdminSidebar>
                {loading ? (
                    <Loader style='table' setLoadingState={setLoading} isLoaded={isLoaded}/>
                ) : (
                    <div>
                        {/* Header */}
                        <div className="flex items-start justify-between mb-6">
                            <div className="flex items-start gap-3">
                                <button
                                    onClick={() => navigate('/taskinstances')}
                                    className="mt-1 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
                                >
                                    <ArrowLeft className="h-5 w-5 text-gray-500" />
                                </button>
                                <div>
                                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                                        {instance.task_flow_name}
                                    </h1>
                                    <p className="text-sm text-gray-500 mt-0.5">
                                        {instance.tenant_user_name}{instance.tenant_address ? ` — ${instance.tenant_address}` : ''}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {instance.tenant_user_role === 'pre_tenancy' && (
                                    <Button variant="outline" size="sm" onClick={handleOpenLinkModal} className="text-blue-600 border-blue-200 hover:bg-blue-50">
                                        <Link2 className="mr-1.5 h-3.5 w-3.5" />
                                        Link to Agreement
                                    </Button>
                                )}
                                <Button variant="outline" size="sm" onClick={() => setAssignSheetOpen(true)}>
                                    <UserPlus className="mr-1.5 h-3.5 w-3.5" />
                                    {instance.owner_admin_user_id ? 'Reassign' : 'Assign'}
                                </Button>
                                <div className="relative">
                                    <Button variant="outline" size="sm" onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}>
                                        <Badge variant={statusInfo.color} className="mr-1.5">{statusInfo.label}</Badge>
                                        <ChevronDown className="h-3.5 w-3.5" />
                                    </Button>
                                    {statusDropdownOpen && (
                                        <>
                                            <div className="fixed inset-0 z-10" onClick={() => setStatusDropdownOpen(false)} />
                                            <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-zinc-800 rounded-lg shadow-lg border border-gray-200 dark:border-zinc-700 py-1 z-20">
                                                {instance.status === 'NOT_STARTED' && (
                                                    <button onClick={() => handleStatusChange('IN_PROGRESS')} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-zinc-700 dark:text-gray-300 flex items-center gap-2">
                                                        <Play className="h-3.5 w-3.5" /> Start
                                                    </button>
                                                )}
                                                {instance.status !== 'COMPLETED' && instance.status !== 'CANCELLED' && (
                                                    <button onClick={() => handleStatusChange('CANCELLED')} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                                                        <XCircle className="h-3.5 w-3.5" /> Cancel
                                                    </button>
                                                )}
                                                <div className="border-t border-gray-100 dark:border-zinc-700 my-1" />
                                                <button onClick={() => { setStatusDropdownOpen(false); setDeleteInstanceModalOpen(true); }} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                                                    <Trash2 className="h-3.5 w-3.5" /> Delete
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Summary bar */}
                        <div className="flex items-center gap-6 mb-6 text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center gap-1.5">
                                <span className="font-medium text-gray-700 dark:text-gray-300">Assigned to:</span>
                                {instance.owner_admin_user_id ? instance.owner_admin_name : <span className="text-amber-600">Unassigned</span>}
                            </div>
                            {instance.due_date && (
                                <div className="flex items-center gap-1.5">
                                    <Clock className="h-3.5 w-3.5" />
                                    <span>Due {formatDate(instance.due_date)}</span>
                                </div>
                            )}
                            <div>
                                {completedCount} of {totalSteps} steps complete
                            </div>
                            <button onClick={handleViewHistory} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:underline">
                                History
                            </button>
                        </div>

                        {/* Two column layout */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                            {/* LEFT: Steps */}
                            <div className="lg:col-span-2 space-y-4">
                                {sortedSteps.map((step, stepIndex) => {
                                    const isAdminStep = step.actor_type_snapshot === 'admin';
                                    const isCompleted = step.status === 'COMPLETED';
                                    const isBlocked = step.status === 'BLOCKED';
                                    const isPendingApproval = step.status === 'PENDING_APPROVAL';
                                    const isSkipped = !step.is_conditionally_visible;
                                    const isActive = !isCompleted && !isBlocked && !isSkipped && !isPendingApproval;
                                    const fields = step.fields || [];

                                    if (isSkipped) {
                                        const skipReason = getSkipReason(step);
                                        return (
                                            <Fragment key={step.id}>
                                                <div className="rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800">
                                                    <div className="px-5 py-3 flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-zinc-700 text-gray-400 text-xs font-semibold">
                                                                {stepIndex + 1}
                                                            </div>
                                                            <div>
                                                                <h3 className="text-sm font-semibold text-gray-400">
                                                                    {step.name_snapshot}
                                                                </h3>
                                                                {skipReason && (
                                                                    <p className="text-xs text-gray-400 mt-0.5">{skipReason}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <span className="text-xs text-gray-400 font-medium">Skipped</span>
                                                    </div>
                                                </div>
                                                {!isInstanceCompleted && !isCancelled && (
                                                    <div className="flex justify-center -mt-2 -mb-2">
                                                        <button
                                                            onClick={() => handleAddStep(step.position)}
                                                            className="text-xs text-gray-300 hover:text-gray-500 flex items-center gap-1 transition-colors py-1"
                                                        >
                                                            <Plus className="h-3 w-3" /> Add step after this
                                                        </button>
                                                    </div>
                                                )}
                                            </Fragment>
                                        );
                                    }

                                    return (
                                        <Fragment key={step.id}>
                                        <div
                                            className={`rounded-xl border transition-all ${
                                                isPendingApproval ? 'border-amber-200 bg-amber-50/30 dark:border-amber-700 dark:bg-amber-900/20' :
                                                isCompleted ? 'border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800' :
                                                isBlocked ? 'border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800 opacity-60' :
                                                'border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800'
                                            }`}
                                        >
                                            {/* Step header */}
                                            <div className="px-5 py-4 flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                                                        isPendingApproval ? 'bg-amber-100 text-amber-600' :
                                                        isCompleted ? 'bg-green-50 text-green-600' :
                                                        isBlocked ? 'bg-gray-100 dark:bg-zinc-700 text-gray-400' :
                                                        'bg-gray-100 dark:bg-zinc-700 text-gray-600 dark:text-gray-400'
                                                    }`}>
                                                        {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : stepIndex + 1}
                                                    </div>
                                                    <div>
                                                        <h3 className={`text-sm font-semibold ${isCompleted ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                                                            {step.name_snapshot}
                                                        </h3>
                                                        {step.description_snapshot && (
                                                            <p className="text-xs text-gray-500 mt-0.5">{step.description_snapshot}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {isPendingApproval && (
                                                        <Badge variant="warning" className="text-xs">Pending Approval</Badge>
                                                    )}
                                                    {isAdminStep && isActive && (
                                                        <Badge variant="outline" className="text-xs">Your action needed</Badge>
                                                    )}
                                                    {!isAdminStep && isActive && (
                                                        <Badge variant="outline" className="text-xs">Tenant</Badge>
                                                    )}
                                                    {isCompleted && (
                                                        <span className="text-xs text-green-600 font-medium">Completed</span>
                                                    )}
                                                    {isCompleted && !isInstanceCompleted && !isCancelled && (
                                                        <button
                                                            onClick={() => handleReopenStep(step)}
                                                            disabled={actionLoading}
                                                            className="text-xs text-gray-400 hover:text-blue-600 flex items-center gap-1 transition-colors"
                                                        >
                                                            <RotateCcw className="h-3 w-3" />
                                                            Reopen
                                                        </button>
                                                    )}
                                                    {isBlocked && (
                                                        <span className="text-xs text-gray-400 font-medium">Upcoming</span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Step content */}
                                            {fields.length > 0 && (
                                                <div className="px-5 pb-4 border-t border-gray-100 dark:border-zinc-700 pt-4">
                                                    {/* Completed step: show submitted values */}
                                                    {isCompleted && (
                                                        <div className="space-y-3">
                                                            {fields.sort((a, b) => (a.position_snapshot || 0) - (b.position_snapshot || 0)).map((field) => (
                                                                <div key={field.id} className="flex items-start gap-3">
                                                                    <div className="w-1/3 min-w-0">
                                                                        <span className="text-xs font-medium text-gray-500">{field.name_snapshot}</span>
                                                                    </div>
                                                                    <div className="flex-1 min-w-0 break-words">
                                                                        <span className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{renderFieldValue(field)}</span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Pending approval step: show submitted values + approve/reject */}
                                                    {isPendingApproval && (
                                                        <div className="space-y-3">
                                                            {fields.sort((a, b) => (a.position_snapshot || 0) - (b.position_snapshot || 0)).map((field) => (
                                                                <div key={field.id} className="flex items-start gap-3">
                                                                    <div className="w-1/3 min-w-0">
                                                                        <span className="text-xs font-medium text-gray-500">{field.name_snapshot}</span>
                                                                    </div>
                                                                    <div className="flex-1 min-w-0 break-words">
                                                                        <span className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{renderFieldValue(field)}</span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                            <div className="flex items-center gap-2 pt-3 border-t border-amber-100">
                                                                <Button
                                                                    onClick={() => handleApproveStep(step)}
                                                                    disabled={actionLoading}
                                                                    className="bg-green-600 hover:bg-green-700 text-white"
                                                                    size="sm"
                                                                >
                                                                    <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                                                                    Approve
                                                                </Button>
                                                                <Button
                                                                    onClick={() => handleRejectStep(step)}
                                                                    disabled={actionLoading}
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="border-red-300 text-red-600 hover:bg-red-50"
                                                                >
                                                                    <XCircle className="mr-1.5 h-3.5 w-3.5" />
                                                                    Reject
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Active tenant step: show what they've submitted so far */}
                                                    {isActive && !isAdminStep && (
                                                        <div className="space-y-3">
                                                            {fields.sort((a, b) => (a.position_snapshot || 0) - (b.position_snapshot || 0)).map((field) => {
                                                                const hasValue = renderFieldValue(field) !== '-';
                                                                return (
                                                                    <div key={field.id} className="flex items-start gap-3">
                                                                        <div className="w-1/3 min-w-0">
                                                                            <span className="text-xs font-medium text-gray-500 inline-flex items-center gap-1">
                                                                                {field.name_snapshot}
                                                                                {field.is_required_snapshot && <span className="text-red-400 ml-0.5">*</span>}
                                                                                {field.help_url_snapshot && (
                                                                                    <a href={field.help_url_snapshot} target="_blank" rel="noopener noreferrer" title="Help" className="text-gray-400 hover:text-gray-600">
                                                                                        <ExternalLink className="h-3 w-3" />
                                                                                    </a>
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex-1 min-w-0 break-words">
                                                                            {hasValue ? (
                                                                                <span className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{renderFieldValue(field)}</span>
                                                                            ) : (
                                                                                <span className="text-sm text-gray-300 italic">Awaiting response</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}

                                                    {/* Active admin step: show editable form */}
                                                    {isActive && isAdminStep && (
                                                        <div className="space-y-4">
                                                            {fields.sort((a, b) => (a.position_snapshot || 0) - (b.position_snapshot || 0)).map((field) => (
                                                                <div key={field.id}>
                                                                    <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                                                        {field.name_snapshot}
                                                                        {field.is_required_snapshot && <span className="text-red-500 ml-0.5">*</span>}
                                                                        {field.help_url_snapshot && (
                                                                            <a href={field.help_url_snapshot} target="_blank" rel="noopener noreferrer" title="Help" className="text-gray-400 hover:text-gray-600">
                                                                                <ExternalLink className="h-3.5 w-3.5" />
                                                                            </a>
                                                                        )}
                                                                    </label>
                                                                    {field.help_text_snapshot && (
                                                                        <p className="text-xs text-gray-400 mb-1.5">{field.help_text_snapshot}</p>
                                                                    )}
                                                                    {renderAdminFieldInput(field)}
                                                                </div>
                                                            ))}
                                                            <div className="pt-2">
                                                                <Button
                                                                    onClick={() => handleAdminSubmitStep(step)}
                                                                    disabled={submittingStep === step.id}
                                                                    className=""
                                                                >
                                                                    {submittingStep === step.id ? 'Submitting...' : 'Submit'}
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Approve/reject buttons for pending approval steps with no fields */}
                                            {isPendingApproval && fields.length === 0 && (
                                                <div className="px-5 pb-4">
                                                    <div className="flex items-center gap-2">
                                                        <Button
                                                            onClick={() => handleApproveStep(step)}
                                                            disabled={actionLoading}
                                                            className="bg-green-600 hover:bg-green-700 text-white"
                                                            size="sm"
                                                        >
                                                            <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                                                            Approve
                                                        </Button>
                                                        <Button
                                                            onClick={() => handleRejectStep(step)}
                                                            disabled={actionLoading}
                                                            variant="outline"
                                                            size="sm"
                                                            className="border-red-300 text-red-600 hover:bg-red-50"
                                                        >
                                                            <XCircle className="mr-1.5 h-3.5 w-3.5" />
                                                            Reject
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Payment / upload info for special steps */}
                                            {step.step_type_snapshot === 'payment' && step.step_config_snapshot && (
                                                <div className="px-5 pb-4 border-t border-gray-100 dark:border-zinc-700 pt-4">
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <CreditCard className="h-4 w-4 text-blue-600" />
                                                        <span className="text-gray-600 dark:text-gray-400">Payment: {step.step_config_snapshot.amount ? `£${step.step_config_snapshot.amount}` : 'Amount TBD'}</span>
                                                        {step.step_result?.status && (
                                                            <Badge variant="outline" className="text-xs">{step.step_result.status}</Badge>
                                                        )}
                                                    </div>
                                                    {!isCompleted && !isInstanceCompleted && !isCancelled && (
                                                        <div className="mt-3">
                                                            <Button
                                                                onClick={() => handleStepStatusChange(step.id, 'COMPLETED')}
                                                                disabled={actionLoading}
                                                                size="sm"
                                                                className="bg-green-600 hover:bg-green-700 text-white"
                                                            >
                                                                <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                                                                Mark Payment Complete
                                                            </Button>
                                                            <p className="text-xs text-gray-400 mt-1.5">Use this if the tenant paid outside of the portal</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {step.step_type_snapshot === 'document_upload' && step.step_result?.files && (() => {
                                                const files = Object.values(step.step_result.files);
                                                const folderUrl = step.step_result.folder_url || null;
                                                return (
                                                    <div className="px-5 pb-4 border-t border-gray-100 dark:border-zinc-700 pt-4">
                                                        <div className="space-y-1">
                                                            {files.map((file, idx) => (
                                                                <div key={idx} className="flex items-center gap-2 text-sm">
                                                                    <FileText className="h-3.5 w-3.5 text-gray-400" />
                                                                    {file.web_url ? (
                                                                        <a href={file.web_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                                                                            {file.filename}
                                                                        </a>
                                                                    ) : (
                                                                        <span className="text-gray-700 dark:text-gray-300">{file.filename}</span>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                        {folderUrl && (
                                                            <a href={folderUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 mt-3">
                                                                <FolderOpen className="h-3.5 w-3.5" />
                                                                Open folder in SharePoint
                                                                <ExternalLink className="h-3 w-3" />
                                                            </a>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                            {step.step_type_snapshot === 'document_signing' && step.step_config_snapshot && (
                                                <div className="px-5 pb-4 border-t border-gray-100 dark:border-zinc-700 pt-4">
                                                    <div className="space-y-1 text-sm">
                                                        <div className="flex items-center gap-2">
                                                            <PenTool className="h-4 w-4 text-amber-600" />
                                                            <span className="text-gray-600 dark:text-gray-400">Template: {step.step_config_snapshot.template_name}</span>
                                                            {step.step_result?.status && (
                                                                <Badge variant="outline" className="text-xs capitalize">{step.step_result.status}</Badge>
                                                            )}
                                                        </div>
                                                        {step.step_config_snapshot.agreement_name && (
                                                            <div className="flex items-center gap-2 pl-6">
                                                                <span className="text-gray-600 dark:text-gray-400">Agreement: {resolveAgreementName(step.step_config_snapshot)}</span>
                                                            </div>
                                                        )}
                                                        {/* Signing order indicator */}
                                                        {step.step_config_snapshot.require_admin_countersign && (
                                                            <div className="flex items-center gap-1.5 pl-6 mt-1">
                                                                <Users className="h-3.5 w-3.5 text-gray-400" />
                                                                <span className="text-xs text-gray-500">
                                                                    Signing order: {(step.step_config_snapshot.signing_order || 'customer_first') === 'admin_first' ? (
                                                                        <span className="text-gray-700 dark:text-gray-300">Admin <ArrowRight className="inline h-3 w-3 text-gray-400" /> Customer</span>
                                                                    ) : (
                                                                        <span className="text-gray-700 dark:text-gray-300">Customer <ArrowRight className="inline h-3 w-3 text-gray-400" /> Admin</span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Not yet initiated or cancelled — allow creating draft */}
                                                    {(!step.step_result?.agreement_id || step.step_result?.status === 'cancelled') && !isCompleted && !isInstanceCompleted && !isCancelled && (
                                                        <div className="mt-3">
                                                            <Button
                                                                onClick={() => handleInitiateDocumentSigning(step)}
                                                                disabled={actionLoading}
                                                                size="sm"
                                                                className="bg-amber-600 hover:bg-amber-700 text-white"
                                                            >
                                                                <PenTool className="mr-1.5 h-3.5 w-3.5" />
                                                                Create Draft
                                                            </Button>
                                                        </div>
                                                    )}

                                                    {/* Draft — review and send */}
                                                    {step.step_result?.agreement_id && step.step_result?.status === 'draft' && !isCompleted && !isInstanceCompleted && !isCancelled && (
                                                        <div className="mt-3 space-y-2">
                                                            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
                                                                <p className="text-xs font-medium text-amber-700 flex-1">Draft agreement created. Review the document and send when ready.</p>
                                                                <TooltipProvider>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <HelpCircle className="h-3.5 w-3.5 text-amber-400 cursor-help shrink-0 mt-0.5" />
                                                                        </TooltipTrigger>
                                                                        <TooltipContent className="max-w-xs">
                                                                            <p className="font-medium mb-1">Preview Document</p>
                                                                            <p className="text-xs mb-2">Opens a PDF with all pre-filled field values visible, so you can verify the document content before sending.</p>
                                                                            <p className="font-medium mb-1">Edit in Adobe Sign</p>
                                                                            <p className="text-xs">Opens the draft in the Adobe Sign console where you can edit fields and layout. Pre-filled values are not shown in this view — they are applied when the document is sent for signing.</p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                </TooltipProvider>
                                                            </div>
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <Button
                                                                    onClick={() => handleReviewDraft(step.id)}
                                                                    disabled={actionLoading}
                                                                    size="sm"
                                                                    variant="outline"
                                                                >
                                                                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                                                                    Preview Document
                                                                </Button>
                                                                {step.step_result?.preview_url && (
                                                                    <Button
                                                                        onClick={() => window.open(step.step_result.preview_url, '_blank')}
                                                                        disabled={actionLoading}
                                                                        size="sm"
                                                                        variant="outline"
                                                                    >
                                                                        <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                                                                        Edit in Adobe Sign
                                                                    </Button>
                                                                )}
                                                                <Button
                                                                    onClick={() => handleSendForSigning(step.id)}
                                                                    disabled={actionLoading}
                                                                    size="sm"
                                                                    className="bg-amber-600 hover:bg-amber-700 text-white"
                                                                >
                                                                    <PenTool className="mr-1.5 h-3.5 w-3.5" />
                                                                    Send for Signing
                                                                </Button>
                                                                <Button
                                                                    onClick={() => handleCancelDocumentSigning(step.id)}
                                                                    disabled={actionLoading}
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="border-red-300 text-red-600 hover:bg-red-50"
                                                                >
                                                                    <XCircle className="mr-1.5 h-3.5 w-3.5" />
                                                                    Discard Draft
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Signing in progress */}
                                                    {step.step_result?.agreement_id && step.step_result?.status === 'sent' && !isCompleted && (
                                                        <div className="mt-3 space-y-2">
                                                            {/* Multi-participant progress */}
                                                            {step.step_result?.participants && (
                                                                <div className="rounded-md border border-gray-200 dark:border-zinc-700 divide-y divide-gray-100 dark:divide-zinc-700 mb-2">
                                                                    {step.step_result.participants
                                                                        .sort((a, b) => a.order - b.order)
                                                                        .map((p, idx) => (
                                                                        <div key={idx} className="flex items-center gap-3 px-3 py-2">
                                                                            <span className="text-xs text-gray-400 font-mono w-4 text-center">{p.order}</span>
                                                                            {p.status === 'signed' ? (
                                                                                <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                                                                            ) : p.status === 'pending' ? (
                                                                                <PenTool className="h-4 w-4 text-amber-500 shrink-0" />
                                                                            ) : (
                                                                                <Clock className="h-4 w-4 text-gray-300 shrink-0" />
                                                                            )}
                                                                            <div className="flex-1 min-w-0">
                                                                                <span className={`text-sm ${p.status === 'signed' ? 'text-green-700' : p.status === 'pending' ? 'text-amber-700 font-medium' : 'text-gray-400'}`}>
                                                                                    {p.role === 'customer' ? 'Customer' : p.role === 'joint_customer' ? 'Joint Signer' : 'Admin'}
                                                                                </span>
                                                                                {p.email && <span className="text-xs text-gray-400 ml-1.5">{p.email}</span>}
                                                                            </div>
                                                                            <Badge variant="outline" className={`text-xs shrink-0 ${p.status === 'signed' ? 'border-green-200 text-green-600' : p.status === 'pending' ? 'border-amber-200 text-amber-600' : 'border-gray-200 text-gray-400'}`}>
                                                                                {p.status === 'signed' ? 'Signed' : p.status === 'pending' ? 'Awaiting signature' : 'Up next'}
                                                                            </Badge>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            <div className="flex items-center gap-2">
                                                                {/* Show Sign Document button when it's admin's turn */}
                                                                {step.step_result?.current_signer === 'admin' && (
                                                                    <Button
                                                                        onClick={() => handleAdminSign(step.id)}
                                                                        disabled={actionLoading}
                                                                        size="sm"
                                                                        className="bg-amber-600 hover:bg-amber-700 text-white"
                                                                    >
                                                                        <PenTool className="mr-1.5 h-3.5 w-3.5" />
                                                                        Sign Document
                                                                    </Button>
                                                                )}
                                                                <Button
                                                                    onClick={() => handleCheckSigningStatus(step.id)}
                                                                    disabled={actionLoading}
                                                                    size="sm"
                                                                    variant="outline"
                                                                >
                                                                    Check Status
                                                                </Button>
                                                                <Button
                                                                    onClick={() => handleCancelDocumentSigning(step.id)}
                                                                    disabled={actionLoading}
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="border-red-300 text-red-600 hover:bg-red-50"
                                                                >
                                                                    <XCircle className="mr-1.5 h-3.5 w-3.5" />
                                                                    Cancel Signing
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Signed */}
                                                    {step.step_result?.status === 'signed' && (
                                                        <p className="text-xs text-green-600 mt-2">
                                                            Signed on {new Date(step.step_result.signed_at).toLocaleDateString()}
                                                        </p>
                                                    )}

                                                    {/* Admin manual complete — only after document has been sent for signing */}
                                                    {step.step_result?.agreement_id && step.step_result?.status === 'sent' && !isCompleted && !isInstanceCompleted && !isCancelled && (
                                                        <div className="mt-3 border-t border-gray-100 dark:border-zinc-700 pt-3">
                                                            <Button
                                                                onClick={() => handleStepStatusChange(step.id, 'COMPLETED')}
                                                                disabled={actionLoading}
                                                                size="sm"
                                                                variant="outline"
                                                                className="border-green-300 text-green-600 hover:bg-green-50"
                                                            >
                                                                <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                                                                Mark as Signed (Manual)
                                                            </Button>
                                                            <p className="text-xs text-gray-400 mt-1.5">Use if the document was signed outside the portal</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {step.step_type_snapshot === 'info' && (
                                                <div className="px-5 pb-4 border-t border-gray-100 dark:border-zinc-700 pt-4">
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <Info className="h-4 w-4 text-blue-600" />
                                                        <span className="text-gray-600 dark:text-gray-400">Information / acknowledgement step</span>
                                                    </div>
                                                    {step.description_snapshot && (
                                                        <p className="text-sm text-gray-500 mt-2">{step.description_snapshot}</p>
                                                    )}
                                                    {isAdminStep && isActive && (
                                                        <div className="pt-3">
                                                            <Button
                                                                onClick={() => handleStepStatusChange(step.id, 'COMPLETED')}
                                                                disabled={actionLoading}
                                                            >
                                                                <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                                                                Acknowledge
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Add field button (not for document_signing or info steps) */}
                                            {(isActive || isPendingApproval) && step.step_type_snapshot === 'standard' && (
                                                <div className="px-5 pb-4">
                                                    <button
                                                        onClick={() => handleAddField(step.id)}
                                                        className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors"
                                                    >
                                                        <Plus className="h-3 w-3" /> Add field
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        {!isInstanceCompleted && !isCancelled && (
                                            <div className="flex justify-center -mt-2 -mb-2">
                                                <button
                                                    onClick={() => handleAddStep(step.position)}
                                                    className="text-xs text-gray-300 hover:text-gray-500 flex items-center gap-1 transition-colors py-1"
                                                >
                                                    <Plus className="h-3 w-3" /> Add step after this
                                                </button>
                                            </div>
                                        )}
                                        </Fragment>
                                    );
                                })}

                                {sortedSteps.length === 0 && (
                                    <div className="text-center py-12 text-sm text-gray-500">
                                        No steps found for this request.
                                    </div>
                                )}

                                {/* Outcome Card */}
                                {instance && !isCancelled && (
                                    <div className={`rounded-xl border transition-all ${
                                        isInstanceCompleted ? 'border-green-200 bg-white dark:bg-zinc-800 dark:border-green-800' :
                                        allVisibleStepsComplete ? 'border-amber-200 bg-white dark:bg-zinc-800 dark:border-amber-700' :
                                        'border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800'
                                    }`}>
                                        {/* Completed state */}
                                        {isInstanceCompleted && (
                                            <div className="px-5 py-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-600">
                                                            <CheckCircle2 className="h-4 w-4" />
                                                        </div>
                                                        <div>
                                                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Outcome</h3>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                                                                {instance.completion_outcome === 'positive' ? 'Request completed' : 'Request closed'}
                                                                {instance.completion_message && <>: "{instance.completion_message}"</>}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {isAutoCompleted && (
                                                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                                                                <Zap className="mr-1 h-3 w-3" />
                                                                Auto-completed
                                                            </Badge>
                                                        )}
                                                        <span className="text-xs text-green-600 font-medium">Completed</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Active state - all steps done, ready for outcome */}
                                        {!isInstanceCompleted && allVisibleStepsComplete && (
                                            <div className="px-5 py-4">
                                                <div className="flex items-center gap-3 mb-4">
                                                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                                                        <Circle className="h-4 w-4" />
                                                    </div>
                                                    <div>
                                                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Outcome</h3>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">All steps have been completed. Choose an outcome for this request.</p>
                                                    </div>
                                                </div>

                                                <div className="flex gap-3 mb-4">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedOutcome('positive');
                                                            setOutcomeMessage(instance.positive_outcome_message || instance.default_completion_message || 'Your request has been completed.');
                                                        }}
                                                        className={`flex-1 text-left rounded-lg border-2 p-3 transition-colors ${
                                                            selectedOutcome === 'positive'
                                                                ? 'border-green-500 bg-green-50 dark:bg-green-900/30'
                                                                : 'border-gray-200 dark:border-zinc-700 hover:border-green-300 hover:bg-green-50/50 dark:hover:bg-green-900/20'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                                                            <div>
                                                                <p className="text-sm font-semibold text-gray-900 dark:text-white">Complete Request</p>
                                                            </div>
                                                        </div>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedOutcome('negative');
                                                            setOutcomeMessage(instance.negative_outcome_message || 'Your request has been closed.');
                                                        }}
                                                        className={`flex-1 text-left rounded-lg border-2 p-3 transition-colors ${
                                                            selectedOutcome === 'negative'
                                                                ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30'
                                                                : 'border-gray-200 dark:border-zinc-700 hover:border-amber-300 hover:bg-amber-50/50 dark:hover:bg-amber-900/20'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <XCircle className="h-5 w-5 text-amber-600 shrink-0" />
                                                            <div>
                                                                <p className="text-sm font-semibold text-gray-900 dark:text-white">Close Request</p>
                                                            </div>
                                                        </div>
                                                    </button>
                                                </div>

                                                {selectedOutcome && (
                                                    <>
                                                        <div className="mb-3">
                                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                                Message to customer
                                                            </label>
                                                            <textarea
                                                                value={outcomeMessage}
                                                                onChange={(e) => setOutcomeMessage(e.target.value)}
                                                                rows={3}
                                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-400 sm:text-sm sm:leading-6 dark:bg-zinc-800"
                                                            />
                                                        </div>
                                                        <div className="flex justify-end">
                                                            <Button
                                                                onClick={handleOutcomeConfirm}
                                                                disabled={outcomeLoading}
                                                            >
                                                                {outcomeLoading ? 'Completing...' : 'Confirm'}
                                                            </Button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {/* Upcoming state - steps still incomplete */}
                                        {!isInstanceCompleted && !allVisibleStepsComplete && (
                                            <div className="px-5 py-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-zinc-700 text-gray-400">
                                                            <Circle className="h-4 w-4" />
                                                        </div>
                                                        <div>
                                                            <h3 className="text-sm font-semibold text-gray-400">Outcome</h3>
                                                            <p className="text-xs text-gray-400">Complete all steps before choosing outcome</p>
                                                        </div>
                                                    </div>
                                                    <span className="text-xs text-gray-400 font-medium">Upcoming</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* RIGHT: Feedback + Messages */}
                            <div className="lg:col-span-1 space-y-4 lg:sticky lg:top-4 lg:self-start">
                                {/* Feedback card */}
                                {instance?.feedback && (
                                    <div className="bg-white dark:bg-zinc-800 rounded-xl border border-gray-200 dark:border-zinc-700 px-4 py-3">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Tenant Feedback</h3>
                                        </div>
                                        <div className="flex items-center gap-1 mb-1">
                                            {[1, 2, 3, 4, 5].map(i => (
                                                <Star
                                                    key={i}
                                                    className={`h-4 w-4 ${i <= instance.feedback.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`}
                                                />
                                            ))}
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-1">{instance.feedback.rating}/5</span>
                                        </div>
                                        {instance.feedback.comment && (
                                            <p className="text-sm text-gray-600 dark:text-gray-400 italic mt-1">{instance.feedback.comment}</p>
                                        )}
                                    </div>
                                )}

                                <div className="bg-white dark:bg-zinc-800 rounded-xl border border-gray-200 dark:border-zinc-700 flex flex-col" style={{ height: instance?.feedback ? 'calc(100vh - 300px)' : 'calc(100vh - 220px)' }}>
                                    {/* Messages header */}
                                    <div className="px-4 py-3 border-b border-gray-100 dark:border-zinc-700 flex items-center gap-2">
                                        <MessageSquare className="h-4 w-4 text-gray-400" />
                                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Messages</h3>
                                        {messages.filter(m => !m.is_read_by_admin).length > 0 && (
                                            <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                                                {messages.filter(m => !m.is_read_by_admin).length}
                                            </span>
                                        )}
                                    </div>

                                    {/* Messages list */}
                                    <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col-reverse gap-3">
                                        {messages.length === 0 ? (
                                            <div className="text-center py-8">
                                                <MessageSquare className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                                                <p className="text-sm text-gray-400">No messages yet</p>
                                            </div>
                                        ) : (
                                            [...messages].reverse().map((msg) => {
                                                const isAdmin = msg.sender_role === 'admin';
                                                return (
                                                    <div
                                                        key={msg.id}
                                                        className={`group relative rounded-lg px-3 py-2.5 text-sm ${
                                                            isAdmin
                                                                ? 'bg-gray-100 dark:bg-zinc-700 ml-6'
                                                                : 'bg-gray-50 dark:bg-zinc-700/50 mr-6 dark:ring-1 dark:ring-zinc-600'
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between mb-1">
                                                            <span className={`text-xs font-medium ${isAdmin ? 'text-gray-700 dark:text-gray-300' : 'text-gray-600 dark:text-gray-400'}`}>
                                                                {isAdmin ? 'You' : msg.sender_name || 'Tenant'}
                                                            </span>
                                                            <div className="flex items-center gap-1">
                                                                <span className="text-xs text-gray-400">
                                                                    {new Date(msg.created_at).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                                </span>
                                                                <button
                                                                    onClick={() => handleDeleteMessage(msg.id)}
                                                                    className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-300 hover:text-red-500 transition-all"
                                                                >
                                                                    <Trash2 className="h-3 w-3" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {msg.subject && (
                                                            <p className="font-medium text-gray-900 dark:text-white text-sm">{msg.subject}</p>
                                                        )}
                                                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{msg.body}</p>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>

                                    {/* Compose */}
                                    {['COMPLETED', 'CANCELLED'].includes(instance?.status) ? (
                                        <div className="border-t border-gray-100 dark:border-zinc-700 px-3 py-3">
                                            <p className="text-xs text-gray-400 text-center">
                                                This request is {instance.status === 'CANCELLED' ? 'cancelled' : 'complete'}. Messaging is closed.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="border-t border-gray-100 dark:border-zinc-700 px-3 py-3">
                                            <div className="flex gap-2">
                                                <textarea
                                                    placeholder="Type a message..."
                                                    value={newMessageBody}
                                                    onChange={(e) => setNewMessageBody(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && !e.shiftKey) {
                                                            e.preventDefault();
                                                            handleSendMessage();
                                                        }
                                                    }}
                                                    rows={1}
                                                    className="flex-1 text-sm border border-gray-200 dark:border-zinc-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent resize-none dark:bg-zinc-800 dark:text-white"
                                                />
                                                <Button
                                                    size="icon"
                                                    onClick={handleSendMessage}
                                                    disabled={!newMessageBody.trim() || sendingMessage}
                                                    className="shrink-0 bg-gray-900 hover:bg-gray-800"
                                                >
                                                    <Send className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Assignment Sheet */}
                <Sheet open={assignSheetOpen} onOpenChange={setAssignSheetOpen}>
                    <SheetContent>
                        <SheetHeader>
                            <SheetTitle>Assign Task Instance</SheetTitle>
                        </SheetHeader>
                        <div className="mt-6">
                            {instance && (
                                <AssignTaskInstanceForm
                                    instanceId={instance.id}
                                    setVisible={setAssignSheetOpen}
                                    onAssigned={refreshData}
                                />
                            )}
                        </div>
                    </SheetContent>
                </Sheet>

                {/* Add Field Sheet */}
                <Sheet open={addFieldSheetOpen} onOpenChange={setAddFieldSheetOpen}>
                    <SheetContent>
                        <SheetHeader>
                            <SheetTitle>Add Field</SheetTitle>
                        </SheetHeader>
                        <div className="mt-6">
                            {selectedStepId && (
                                <AddInstanceFieldForm
                                    stepId={selectedStepId}
                                    setVisible={setAddFieldSheetOpen}
                                    onFieldAdded={refreshData}
                                />
                            )}
                        </div>
                    </SheetContent>
                </Sheet>

                {/* Assignment History Sheet */}
                <Sheet open={historySheetOpen} onOpenChange={setHistorySheetOpen}>
                    <SheetContent>
                        <SheetHeader>
                            <SheetTitle>Assignment History</SheetTitle>
                        </SheetHeader>
                        <div className="mt-6 space-y-4">
                            {assignmentHistory.length > 0 ? (
                                assignmentHistory.map((entry, idx) => (
                                    <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-zinc-800 rounded-md border border-gray-200 dark:border-zinc-700">
                                        <Clock className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-gray-900 dark:text-white">
                                                {entry.previous_owner_name
                                                    ? `Reassigned from ${entry.previous_owner_name} to ${entry.new_owner_name || 'Unassigned'}`
                                                    : `Assigned to ${entry.new_owner_name || 'Unassigned'}`
                                                }
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">by {entry.changed_by_name}</p>
                                            {entry.note && (
                                                <p className="text-xs text-gray-500 mt-1">{entry.note}</p>
                                            )}
                                            <p className="text-xs text-gray-400 mt-1">{formatDateTime(entry.changed_at)}</p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-gray-500">No assignment history.</p>
                            )}
                        </div>
                    </SheetContent>
                </Sheet>

                {/* Add Step Sheet */}
                <Sheet open={addStepSheetOpen} onOpenChange={setAddStepSheetOpen}>
                    <SheetContent>
                        <SheetHeader>
                            <SheetTitle>Add Step</SheetTitle>
                        </SheetHeader>
                        <div className="mt-6">
                            {instance && (
                                <AddInstanceStepForm
                                    instanceId={instance.id}
                                    insertAfterPosition={addStepAfterPosition}
                                    setVisible={setAddStepSheetOpen}
                                    onStepAdded={refreshData}
                                />
                            )}
                        </div>
                    </SheetContent>
                </Sheet>

                {/* Completion Message Modal */}
                <CompletionMessageModal
                    open={completionModalOpen}
                    setOpen={setCompletionModalOpen}
                    defaultMessage={instance?.default_completion_message || ''}
                    onConfirm={(outcome, message) => pendingLastStep ? handleCompleteLastStep(outcome, message) : handleStatusCompleteWithOutcome(outcome, message)}
                    loading={completionModalLoading}
                />

                {/* Review Step Modal */}
                <ReviewStepModal
                    open={reviewModalOpen}
                    setOpen={setReviewModalOpen}
                    defaultCloseMessage={instance?.negative_outcome_message || 'Your request has been closed.'}
                    onConfirm={handleReviewConfirm}
                    loading={reviewLoading}
                />

                {/* Reopen Step Modal */}
                <ReopenStepModal
                    open={reopenModalOpen}
                    setOpen={setReopenModalOpen}
                    stepName={reopenStep?.name_snapshot || ''}
                    reason={reopenReason}
                    setReason={setReopenReason}
                    onConfirm={handleReopenConfirm}
                    loading={reopenLoading}
                />

                {/* Link to Agreement Modal */}
                <LinkToAgreementModal
                    open={linkModalOpen}
                    setOpen={setLinkModalOpen}
                    fields={linkableFields}
                    tenantUser={linkTenantUser}
                    onConfirm={handleLinkConfirm}
                    loading={linkLoading}
                    fieldsLoading={linkFieldsLoading}
                />

                {/* Delete Field Modal */}
                {/* Send for Signing Confirmation Modal */}
                <Transition.Root show={signingConfirmModalOpen} as={Fragment}>
                    <Dialog as="div" className="relative z-10" onClose={setSigningConfirmModalOpen}>
                        <Transition.Child as={Fragment} enter="ease-out duration-100" enterFrom="opacity-0" enterTo="opacity-90" leave="ease-in duration-100" leaveFrom="opacity-90" leaveTo="opacity-0">
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                        </Transition.Child>
                        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                            <div className="flex min-h-full items-start justify-center p-4 text-center sm:items-center sm:p-0">
                                <Transition.Child as={Fragment} enter="ease-out duration-100" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-100" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                    <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-zinc-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                        <div>
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
                                                    <PenTool className="h-5 w-5 text-amber-600" />
                                                </div>
                                                <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                                                    Create Draft Agreement
                                                </Dialog.Title>
                                            </div>

                                            {signingConfirmStep?.step_config_snapshot && (
                                                <div className="space-y-4">
                                                    <div className="rounded-md bg-gray-50 dark:bg-zinc-700 px-3 py-2">
                                                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Template</p>
                                                        <p className="text-sm text-gray-900 dark:text-white mt-0.5">{signingConfirmStep.step_config_snapshot.template_name}</p>
                                                    </div>
                                                    <div className="rounded-md bg-gray-50 dark:bg-zinc-700 px-3 py-2">
                                                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Agreement Name</p>
                                                        <p className="text-sm text-gray-900 dark:text-white mt-0.5">{resolveAgreementName(signingConfirmStep.step_config_snapshot)}</p>
                                                    </div>
                                                    {signingConfirmStep.step_config_snapshot.require_admin_countersign && (() => {
                                                        const isCustomerFirst = (signingConfirmStep.step_config_snapshot.signing_order || 'customer_first') === 'customer_first';
                                                        return (
                                                            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
                                                                <p className="text-xs font-medium text-amber-700 uppercase tracking-wide flex items-center gap-1.5">
                                                                    <Users className="h-3.5 w-3.5" />
                                                                    Signing Order
                                                                </p>
                                                                <div className="flex items-center gap-2 mt-1.5">
                                                                    <span className={`text-sm font-medium ${isCustomerFirst ? 'text-amber-800' : 'text-gray-500'}`}>
                                                                        {isCustomerFirst ? '1. Customer' : '1. Admin (you)'}
                                                                    </span>
                                                                    <ArrowRight className="h-3.5 w-3.5 text-amber-400" />
                                                                    <span className={`text-sm font-medium ${isCustomerFirst ? 'text-gray-500' : 'text-amber-800'}`}>
                                                                        {isCustomerFirst ? '2. Admin (you)' : '2. Customer'}
                                                                    </span>
                                                                </div>
                                                                <p className="text-xs text-amber-600 mt-1">
                                                                    {isCustomerFirst
                                                                        ? 'The customer will sign first. You will be prompted to counter-sign after.'
                                                                        : 'You will sign first. The customer will then be prompted to sign.'}
                                                                </p>
                                                            </div>
                                                        );
                                                    })()}

                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Joint Customer Email <span className="font-normal normal-case">(optional)</span></label>
                                                        <input
                                                            type="email"
                                                            value={jointCustomerEmail}
                                                            onChange={(e) => setJointCustomerEmail(e.target.value)}
                                                            placeholder="e.g. partner or spouse email address"
                                                            className="block w-full rounded-md border-0 py-2 px-3 text-sm text-gray-900 dark:text-white dark:bg-zinc-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600"
                                                        />
                                                        <p className="text-xs text-gray-400 mt-1">If provided, this person will also receive the document to sign via Adobe Sign.</p>
                                                    </div>

                                                    {(signingConfirmStep.step_config_snapshot.field_mappings || []).length > 0 && (
                                                        <div>
                                                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Template Fields</p>
                                                            <div className="border border-gray-200 dark:border-zinc-700 rounded-md divide-y divide-gray-200 dark:divide-zinc-700">
                                                                {signingConfirmStep.step_config_snapshot.field_mappings.map((mapping, idx) => {
                                                                    const value = resolveFieldMappingValue(mapping);
                                                                    return (
                                                                        <div key={idx} className="px-3 py-2 flex items-start justify-between gap-3">
                                                                            <div className="min-w-0">
                                                                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{mapping.adobe_field_name}</p>
                                                                                <p className="text-xs text-gray-400">
                                                                                    {mapping.source_type === 'task_data' ? 'From task data' : mapping.source_type === 'static_value' ? 'Static value' : 'Customer enters'}
                                                                                </p>
                                                                            </div>
                                                                            <div className="text-right shrink-0">
                                                                                {value === null ? (
                                                                                    <span className="text-xs text-gray-400 italic">Customer fills in</span>
                                                                                ) : value === '-' || value === '(step not found)' || value === '(field not found)' ? (
                                                                                    <span className="text-xs text-red-500">{value}</span>
                                                                                ) : (
                                                                                    <span className="text-sm text-gray-900 dark:text-white">{value}</span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                            <button
                                                type="button"
                                                className="inline-flex w-full justify-center rounded-md bg-amber-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-500 sm:ml-3 sm:w-auto"
                                                onClick={confirmInitiateDocumentSigning}
                                                tabIndex="-1"
                                            >
                                                Create Draft
                                            </button>
                                            <button
                                                type="button"
                                                className="inline-flex w-full justify-center rounded-md bg-white dark:bg-zinc-800 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 hover:bg-gray-50 dark:hover:bg-zinc-700 sm:mt-0 sm:w-auto"
                                                onClick={() => setSigningConfirmModalOpen(false)}
                                                tabIndex="-1"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </Dialog>
                </Transition.Root>

                <ConfirmationModal
                    open={cancelSigningModalOpen}
                    setOpen={setCancelSigningModalOpen}
                    title="Cancel Document Signing"
                    message="Are you sure you want to cancel this document signing? The agreement will be cancelled in Adobe Sign and the step will be reset."
                    confirmText="Cancel Signing"
                    cancelText="Go Back"
                    onConfirm={confirmCancelDocumentSigning}
                    variant="danger"
                />
                <DeleteModal
                    open={deleteModalOpen}
                    setOpen={setDeleteModalOpen}
                    IDToDelete={fieldToDelete?.id}
                    refresh={refreshData}
                    deleteAction="deleteInstanceField"
                    title="Delete Field"
                    message={`Are you sure you want to delete "${fieldToDelete?.name_snapshot}"? This action cannot be undone.`}
                />
                <ConfirmationModal
                    open={deleteInstanceModalOpen}
                    setOpen={setDeleteInstanceModalOpen}
                    title="Delete Task Instance"
                    message="Are you sure you want to permanently delete this task instance? All steps, fields, messages, and history will be removed. This action cannot be undone."
                    note="Any files uploaded to SharePoint will not be deleted and must be removed manually."
                    confirmText="Delete"
                    cancelText="Go Back"
                    onConfirm={handleDeleteInstance}
                    variant="danger"
                />
            </AdminSidebar>
        </>
    );
}
